import { API } from "@/app/api";
import {
  getCurrentTimeTimestamp,
  getNearestHourTimestamp,
} from "@/data-converters/metrics-pipe";
import {
  IFoundBlocks,
  IPoolLuck,
  IPoolStats,
  IPoolStatsHistory,
} from "@/data/api/main-data";
import {
  IDomainFoundBlock,
  IDomainPoolStat,
  IDomainPoolStatHistories,
} from "@/data/domain/data";
import {
  mapApiToDomainFoundBlocks,
  mapToDomainPoolLuck,
  mapToDomainPoolStats,
  mapToDomainPoolStatsHistory,
} from "@/data/domain/mapper";
import { buildSafeHistoryInterval } from "./history-interval";
import {
  getResponseStatus,
  normalizeErrorStatus,
  postApi,
} from "./post-api";

export const MAIN_HISTORY_MAX_INTERVALS = 70;
const POOL_LUCK_INTERVALS = [
  86_400,
  259_200,
  604_800,
  864_000,
  2_592_000,
  7_776_000,
];
const DEFAULT_FOUND_BLOCKS_COUNT = 100;

interface IFetchPoolStatsHistoryOptions {
  timeFrom?: number;
  overlapIntervals?: number;
}

export async function fetchPoolStats(
  coin: string,
  successCallback: (
    value: IDomainPoolStat[],
    lastValuePower: number,
    currentTime: number
  ) => void,
  errorCallback: (status: string) => void
) {
  await postApi<IPoolStats>(API.Query.PoolStats, { coin: coin })
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        const result: IDomainPoolStat[] = mapToDomainPoolStats(response);
        const resolvedCurrentTime = resolveCurrentTime(response.currentTime);
        const firstStat = Array.isArray(response.stats) ? response.stats[0] : undefined;
        const lastValuePower =
          firstStat != undefined
            ? firstStat.power * Math.pow(10, firstStat.powerMultLog10)
            : 0;
        successCallback(
          result,
          lastValuePower,
          resolvedCurrentTime
        );
      } else {
        errorCallback(getResponseStatus(response));
      }
    })
    .catch((reason) => {
      console.warn(`fetchPoolStats catch error: ${reason}`);
      errorCallback(normalizeErrorStatus(reason));
    });
}

export async function fetchPoolLuck(
  coin: string,
  successCallback: (value: IDomainPoolStat[]) => void,
  errorCallback: (status: string) => void
) {
  await postApi<IPoolLuck>(API.Query.PoolLuck, {
    coin: coin,
    intervals: POOL_LUCK_INTERVALS,
  })
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        const result = mapToDomainPoolLuck(response);
        successCallback(result);
      } else {
        errorCallback(getResponseStatus(response));
      }
    })
    .catch((reason) => {
      console.warn(`fetch pool luck error: ${reason}`);
      errorCallback(normalizeErrorStatus(reason));
    });
}

export async function fetchFoundBlocks(
  coin: string,
  locale: string | undefined,
  successCallback: (value: IDomainFoundBlock[]) => void,
  errorCallback: (status: string) => void
) {
  await postApi<IFoundBlocks>(API.Query.FoundBlocks, {
    coin: coin,
    count: DEFAULT_FOUND_BLOCKS_COUNT,
  })
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        const foundBLocks = mapApiToDomainFoundBlocks(locale, response);
        successCallback(foundBLocks);
      } else {
        errorCallback(getResponseStatus(response));
      }
    })
    .catch((reason) => {
      errorCallback(normalizeErrorStatus(reason));
    });
}

export async function fetchFoundBlocksWithCount(
  coin: string,
  count: number,
  locale: string | undefined,
  successCallback: (value: IDomainFoundBlock[]) => void,
  errorCallback: (status: string) => void
) {
  await postApi<IFoundBlocks>(API.Query.FoundBlocks, {
    coin: coin,
    count: count,
  })
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        const foundBLocks = mapApiToDomainFoundBlocks(locale, response);
        successCallback(foundBLocks);
      } else {
        errorCallback(getResponseStatus(response));
      }
    })
    .catch((reason) => {
      errorCallback(normalizeErrorStatus(reason));
    });
}

export async function fetchFoundBlocksWithOffset(
  coin: string,
  count: number,
  locale: string | undefined,
  successCallback: (value: IDomainFoundBlock[]) => void,
  errorCallback: (status: string) => void
) {
  await postApi<IFoundBlocks>(API.Query.FoundBlocks, {
    coin: coin,
    count: count,
  })
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        const foundBLocks = mapApiToDomainFoundBlocks(locale, response);
        successCallback(foundBLocks);
      } else {
        errorCallback(getResponseStatus(response));
      }
    })
    .catch((reason) => {
      errorCallback(normalizeErrorStatus(reason));
    });
}

export async function fetchFoundBlocksWithOffsetHash(
  coin: string,
  count: number,
  locale: string | undefined,
  hashFrom: string,
  heightFrom: number,
  successCallback: (value: IDomainFoundBlock[]) => void,
  errorCallback: (status: string) => void
) {
  await postApi<IFoundBlocks>(API.Query.FoundBlocks, {
    coin: coin,
    count: count,
    hashFrom: hashFrom,
    heightFrom: heightFrom,
  })
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        const foundBLocks = mapApiToDomainFoundBlocks(locale, response);
        successCallback(foundBLocks);
      } else {
        errorCallback(getResponseStatus(response));
      }
    })
    .catch((reason) => {
      errorCallback(normalizeErrorStatus(reason));
    });
}

export async function fetchPoolStatsHistory(
  coin: string,
  timeInterval: number,
  successCallback: (
    value: IDomainPoolStatHistories,
    currentTime: number
  ) => void,
  errorCallback: (status: string) => void,
  options?: IFetchPoolStatsHistoryOptions
) {
  const startTime = getNearestHourTimestamp();
  const safeTimeInterval =
    Number.isFinite(timeInterval) && timeInterval > 0
      ? Math.floor(timeInterval)
      : 1;
  const safeOverlapIntervals =
    Number.isFinite(options?.overlapIntervals) &&
    (options?.overlapIntervals ?? 0) >= 0
      ? Math.floor(options?.overlapIntervals ?? 0)
      : 1;
  const incrementalTimeFrom =
    options?.timeFrom != undefined && Number.isFinite(options.timeFrom)
      ? Math.floor(options.timeFrom) - safeTimeInterval * safeOverlapIntervals
      : undefined;
  const resolvedTimeFrom =
    incrementalTimeFrom != undefined
      ? incrementalTimeFrom
      : startTime - safeTimeInterval * MAIN_HISTORY_MAX_INTERVALS;
  const safePoolStatsInterval = buildSafeHistoryInterval({
    rawTimeFrom: resolvedTimeFrom,
    groupByInterval: safeTimeInterval,
    maxIntervals: MAIN_HISTORY_MAX_INTERVALS,
    roundTimeToUpByGroup: true,
  });

  // fetch(API.Query.PoolStatsHistory, {
  //   method: "POST",
  //   body: JSON.stringify({
  //     coin: coin,
  //     groupByInterval: timeInterval,
  //     timeFrom: timeFrom,
  //   }),
  // })
  await postApi<IPoolStatsHistory>(API.Query.PoolStatsHistory, {
    coin: coin,
    groupByInterval: safePoolStatsInterval.groupByInterval,
    timeFrom: safePoolStatsInterval.timeFrom,
    timeTo: safePoolStatsInterval.timeTo,
  })
    .then((response) => {
      if (response && response.status == API.Status.Success) {
        const result: IDomainPoolStatHistories =
          mapToDomainPoolStatsHistory(response);
        successCallback(result, resolveCurrentTime(response.currentTime));
      } else {
        errorCallback(getResponseStatus(response));
      }
    })
    .catch((reason) => {
      errorCallback(normalizeErrorStatus(reason));
    });
}

function resolveCurrentTime(rawCurrentTime: number): number {
  const value = Number(rawCurrentTime);
  if (Number.isFinite(value) && value > 0) return Math.floor(value);
  return getCurrentTimeTimestamp();
}
